import { orderBy } from 'lodash/fp';
import { FC, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom-v5-compat';

import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import NullState from 'dashboard/components-lib/NullState';
import { Backdrop } from 'dashboard/layouts/ShowViewLayoutV2';
import { ConnectionNode } from 'dashboard/models/platform-node';
import { useGetPlatformConnectionsQuery } from 'dashboard/redux/api/platform-node';
import DashboardPlatformConnectionsShowView from 'dashboard/views/DashboardPlatformConnectionsShowView';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import useRTKErrorHandler from 'lib/hooks/useRTKErrorHandler';

type Params = { platformNodeId: string };

const sortConnections = (connectionNodes: ConnectionNode[]) => {
  return orderBy(['type', 'metadata.name'], ['asc', 'asc'], connectionNodes);
};
const DashboardPlatformConnectionsShowRoute: FC = () => {
  const navigate = useNavigate();
  const { platformNodeId } = useParams<Params>() as Params;
  const isPlatformConnectionDashboardEnabled = useFeatureFlag(
    'global:enable-platform-connection-dashboard',
  );

  useEffect(() => {
    if (!isPlatformConnectionDashboardEnabled) {
      navigate(`/dashboard/home/`, { replace: true });
    }
  }, [isPlatformConnectionDashboardEnabled, navigate]);

  const platformConnectionQuery = useGetPlatformConnectionsQuery(platformNodeId, {
    refetchOnMountOrArgChange: true,
  });

  const {
    data: platformConnectionQueryResponse,
    error: platformConnectionQueryError,
    isLoading: platformConnectionQueryIsLoading,
  } = platformConnectionQuery;

  useRTKErrorHandler({
    error: platformConnectionQueryError,
    onError: () => ({ message: 'Failed to load platform connections' }),
  });

  if (platformConnectionQueryIsLoading) {
    return (
      <Backdrop>
        <LoadingIndicator fullscreen />
      </Backdrop>
    );
  }

  if (platformConnectionQueryError || !platformConnectionQueryResponse?.rootNode) {
    return (
      <Backdrop>
        <NullState message='Failed to compute platform connections for that template' />
      </Backdrop>
    );
  }

  return (
    <DashboardPlatformConnectionsShowView
      rootNode={platformConnectionQueryResponse.rootNode}
      inboundConnections={sortConnections(platformConnectionQueryResponse.inboundConnections)}
      outboundConnections={sortConnections(platformConnectionQueryResponse.outboundConnections)}
    />
  );
};

export default DashboardPlatformConnectionsShowRoute;
